import * as RadixDialog from "@radix-ui/react-dialog";
import { useEffect, useId, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/primitives/Button";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import type { TableColumnVisibilityColumn, ViewFilter, ViewType, SaveViewPayload, SaveViewResult } from "./DataTable.types";

export type TableSaveViewProps = {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  trigger?: ReactNode;
  triggerLabel?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  viewName?: string;
  defaultViewName?: string;
  onViewNameChange?: (viewName: string) => void;
  viewNameLabel?: ReactNode;
  viewNamePlaceholder?: string;
  appliedLabel?: ReactNode;
  appliedSettings?: ReactNode;
  appliedSettingLabels?: string[];
  columns?: TableColumnVisibilityColumn[];
  visibleColumnIds?: string[];
  hiddenColumnCount?: number;
  filtersCount?: number;
  /** The listing type — required to build the API payload. */
  viewType?: ViewType;
  /** Active filters to persist with the view, in API format. */
  filters?: ViewFilter[];
  /** View priority. Higher wins on resolution. Defaults to 0. */
  priority?: number;
  cancelLabel?: ReactNode;
  saveLabel?: ReactNode;
  onCancel?: () => void;
  /**
   * Called on submit with the API-formatted payload.
   * Return a `SaveViewResult` (e.g. from `saveView()`) to trigger `onSaved`
   * and allow the component to detect duplicates.
   */
  onSave?: (payload: SaveViewPayload) => void | Promise<SaveViewResult | void>;
  /** Fires after a successful save with the resolved view and duplicate flag. */
  onSaved?: (result: SaveViewResult) => void;
  loading?: boolean;
  disabled?: boolean;
  closeOnSave?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  preventClose?: boolean;
  className?: string;
  triggerClassName?: string;
};

const isPromiseLike = (value: unknown): value is Promise<unknown> =>
  Boolean(value) && typeof (value as { then?: unknown }).then === "function";

const countHiddenColumns = (columns?: TableColumnVisibilityColumn[], visibleColumnIds?: string[]) => {
  if (!columns || !visibleColumnIds) return 0;

  const visibleIds = new Set(visibleColumnIds);
  return columns.filter((column) => !visibleIds.has(column.id)).length;
};

export function TableSaveView({
  open,
  defaultOpen,
  onOpenChange,
  onClose,
  trigger,
  triggerLabel = "Save",
  title = "Save this view",
  description = "Give your custom view a name to save it for later",
  viewName,
  defaultViewName = "",
  onViewNameChange,
  viewNameLabel = "View Name",
  viewNamePlaceholder = "e.g. High Priority Orders",
  appliedLabel = "APPLIED FILTERS & SETTINGS",
  appliedSettings,
  appliedSettingLabels,
  columns,
  visibleColumnIds = [],
  hiddenColumnCount,
  filtersCount = 0,
  viewType,
  filters,
  priority = 0,
  cancelLabel = "Cancel",
  saveLabel = "Save view",
  onCancel,
  onSave,
  onSaved,
  loading,
  disabled,
  closeOnSave = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  preventClose = false,
  className,
  triggerClassName
}: TableSaveViewProps) {
  const inputId = useId();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen ?? false);
  const [internalViewName, setInternalViewName] = useState(defaultViewName);
  const [internalLoading, setInternalLoading] = useState(false);

  const activeOpen = open ?? uncontrolledOpen;
  const activeViewName = viewName ?? internalViewName;
  const activeLoading = Boolean(loading || internalLoading);
  const activeHiddenColumnCount = hiddenColumnCount ?? countHiddenColumns(columns, visibleColumnIds);
  const submitDisabled = disabled || activeLoading || activeViewName.trim().length === 0;

  const fallbackSettingLabels = useMemo(() => {
    const labels: string[] = [];
    if (activeHiddenColumnCount > 0) labels.push(`Columns hidden (${activeHiddenColumnCount})`);
    if (filtersCount > 0) labels.push(`Filters applied (${filtersCount})`);
    return labels;
  }, [activeHiddenColumnCount, filtersCount]);
  const settingLabels = appliedSettingLabels ?? fallbackSettingLabels;

  useEffect(() => {
    if (activeOpen && viewName === undefined) {
      setInternalViewName(defaultViewName);
    }
  }, [activeOpen, defaultViewName, viewName]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && preventClose) return;
    if (open === undefined) setUncontrolledOpen(nextOpen);
    onOpenChange?.(nextOpen);
    if (!nextOpen) onClose?.();
  };

  const updateViewName = (nextViewName: string) => {
    if (viewName === undefined) setInternalViewName(nextViewName);
    onViewNameChange?.(nextViewName);
  };

  const handleCancel = () => {
    onCancel?.();
    handleOpenChange(false);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = activeViewName.trim();
    if (!name || submitDisabled) return;

    const payload: SaveViewPayload = {
      view_type: viewType ?? "orders",
      name,
      columns: (columns ?? []).map((col, index) => ({
        key: col.id,
        order: index,
        visible: visibleColumnIds.includes(col.id),
      })),
      filters: filters ?? [],
      priority,
    };

    const result = onSave?.(payload);

    if (isPromiseLike(result)) {
      setInternalLoading(true);
      void result
        .then((resolved) => {
          if (resolved && typeof resolved === "object" && "view" in resolved) {
            onSaved?.(resolved as SaveViewResult);
          }
        })
        .finally(() => {
          setInternalLoading(false);
          if (closeOnSave) handleOpenChange(false);
        });
      return;
    }

    if (closeOnSave) handleOpenChange(false);
  };

  return (
    <RadixDialog.Root open={activeOpen} onOpenChange={handleOpenChange}>
      {trigger !== null && (
        <RadixDialog.Trigger asChild>
          {trigger ?? (
            <Button size="sm" className={triggerClassName}>
              {triggerLabel}
            </Button>
          )}
        </RadixDialog.Trigger>
      )}
      <RadixDialog.Portal>
        <BfrsPortalScope>
          <RadixDialog.Overlay className="bfrs-fixed bfrs-inset-0 bfrs-z-50 bfrs-bg-text-primary/45 bfrs-animate-overlay-in" />
          <RadixDialog.Content
            className={cn(
              "bfrs-fixed bfrs-left-1/2 bfrs-top-1/2 bfrs-z-50 bfrs-max-h-[calc(100dvh-2rem)] bfrs-w-[calc(100%-2rem)] bfrs-max-w-[420px] -bfrs-translate-x-1/2 -bfrs-translate-y-1/2 bfrs-overflow-auto bfrs-rounded-lg bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-p-4 bfrs-text-text-primary bfrs-shadow-overlay bfrs-outline-none bfrs-animate-modal-content-in sm:bfrs-p-5",
              className
            )}
            onPointerDownOutside={(event) => {
              if (!closeOnOverlayClick || preventClose) event.preventDefault();
            }}
            onEscapeKeyDown={(event) => {
              if (!closeOnEscape || preventClose) event.preventDefault();
            }}
          >
            <form className="bfrs-flex bfrs-flex-col" onSubmit={handleSubmit}>
              <header>
                <RadixDialog.Title className="bfrs-text-lg bfrs-font-bold bfrs-leading-6 bfrs-text-text-primary">
                  {title}
                </RadixDialog.Title>
                {description && (
                  <RadixDialog.Description className="bfrs-mt-1 bfrs-text-xs bfrs-leading-5 bfrs-text-text-muted">
                    {description}
                  </RadixDialog.Description>
                )}
              </header>

              <div className="bfrs-mt-5">
                <label htmlFor={inputId} className="bfrs-block bfrs-text-xs bfrs-font-bold bfrs-leading-5 bfrs-text-text-primary">
                  {viewNameLabel}
                </label>
                <input
                  id={inputId}
                  autoFocus
                  value={activeViewName}
                  placeholder={viewNamePlaceholder}
                  disabled={disabled || activeLoading}
                  className="bfrs-mt-1.5 bfrs-h-10 bfrs-w-full bfrs-rounded-md bfrs-border-[1.5px] bfrs-border-border-focus bfrs-bg-surface-card bfrs-px-3 bfrs-text-xs bfrs-font-medium bfrs-leading-none bfrs-text-text-primary bfrs-outline-none bfrs-transition placeholder:bfrs-text-text-muted focus:bfrs-ring-2 focus:bfrs-ring-primary-soft focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-primary-soft disabled:bfrs-cursor-not-allowed disabled:bfrs-bg-surface-muted disabled:bfrs-text-text-disabled"
                  onChange={(event) => updateViewName(event.target.value)}
                />
              </div>

              <section className="bfrs-mt-5" aria-label={typeof appliedLabel === "string" ? appliedLabel : "Applied filters and settings"}>
                <div className="bfrs-text-[11px] bfrs-font-bold bfrs-leading-4 bfrs-tracking-[0.12em] bfrs-text-text-muted">
                  {appliedLabel}
                </div>
                <div className="bfrs-mt-2 bfrs-flex bfrs-flex-wrap bfrs-gap-2">
                  {appliedSettings ??
                    (settingLabels.length > 0 ? (
                      settingLabels.map((label) => (
                        <span key={label} className="bfrs-inline-flex bfrs-min-h-7 bfrs-items-center bfrs-rounded-md bfrs-bg-surface-app bfrs-px-2.5 bfrs-py-1 bfrs-text-xs bfrs-font-bold bfrs-leading-none bfrs-text-text-primary">
                          {label}
                        </span>
                      ))
                    ) : (
                      <span className="bfrs-inline-flex bfrs-min-h-7 bfrs-items-center bfrs-rounded-md bfrs-bg-surface-app bfrs-px-2.5 bfrs-py-1 bfrs-text-xs bfrs-font-bold bfrs-leading-none bfrs-text-text-muted">
                        No changes applied
                      </span>
                    ))}
                </div>
              </section>

              <div className="bfrs-mt-6 bfrs-grid bfrs-grid-cols-1 bfrs-gap-2.5 sm:bfrs-grid-cols-2">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={activeLoading}
                  className="bfrs-h-10 bfrs-rounded-md bfrs-border-[1.5px] bfrs-border-border-focus bfrs-bg-surface-card bfrs-text-xs bfrs-font-bold bfrs-text-text-primary hover:bfrs-bg-surface-card active:bfrs-bg-surface-muted"
                  onClick={handleCancel}
                >
                  {cancelLabel}
                </Button>
                <Button
                  type="submit"
                  loading={activeLoading}
                  disabled={submitDisabled}
                  className="bfrs-h-10 bfrs-rounded-md bfrs-bg-text-muted bfrs-text-xs bfrs-font-bold bfrs-text-text-inverse hover:bfrs-bg-text-secondary active:bfrs-bg-text-primary"
                >
                  {saveLabel}
                </Button>
              </div>
            </form>
          </RadixDialog.Content>
        </BfrsPortalScope>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
